import { useTranslation } from "@/packages/utils";
import { Check, X, ArrowUp, ArrowDown } from "lucide-react";
import { ConfirmDialog } from "@/components/ui";
import { TIER_FEATURES, TIER_ORDER } from "@/packages/features-billing";
import type { PaidTier } from "@/packages/features-billing";
import type { BillingTier } from "@/packages/domains/billing";

interface PlanChangeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  currentTier: BillingTier;
  targetTier: PaidTier;
}

/**
 * Compute which features are gained when moving from currentTier to targetTier.
 * A feature is "gained" if it's included in target but not in current.
 */
function getGainedFeatures(
  currentTier: BillingTier,
  targetTier: PaidTier,
): string[] {
  const targetFeatures = TIER_FEATURES[targetTier];
  const targetIncluded = new Set(
    targetFeatures.filter((f) => f.included).map((f) => f.labelKey),
  );

  if (currentTier === "free") {
    return [...targetIncluded];
  }

  const currentFeatures = TIER_FEATURES[currentTier as PaidTier];
  if (!currentFeatures) return [...targetIncluded];

  const currentIncluded = new Set(
    currentFeatures.filter((f) => f.included).map((f) => f.labelKey),
  );

  return [...targetIncluded].filter((key) => !currentIncluded.has(key));
}

/**
 * Compute which features are lost when moving from currentTier to targetTier.
 * A feature is "lost" if it's included in current but not in target.
 */
function getLostFeatures(
  currentTier: BillingTier,
  targetTier: PaidTier,
): string[] {
  if (currentTier === "free") return [];

  const currentFeatures = TIER_FEATURES[currentTier as PaidTier];
  if (!currentFeatures) return [];

  const currentIncluded = new Set(
    currentFeatures.filter((f) => f.included).map((f) => f.labelKey),
  );

  const targetFeatures = TIER_FEATURES[targetTier];
  const targetIncluded = new Set(
    targetFeatures.filter((f) => f.included).map((f) => f.labelKey),
  );

  return [...currentIncluded].filter((key) => !targetIncluded.has(key));
}

function useFeatureLabels() {
  const { t } = useTranslation(["settings"]);

  return {
    audits3: t("settings:billing.pricing.tierFeatures.audits3"),
    audits5: t("settings:billing.pricing.tierFeatures.audits5"),
    auditsUnlimited25Users: t(
      "settings:billing.pricing.tierFeatures.auditsUnlimited25Users",
    ),
    workspace1User1: t("settings:billing.pricing.tierFeatures.workspace1User1"),
    workspace3User5: t("settings:billing.pricing.tierFeatures.workspace3User5"),
    workspacesUnlimited: t(
      "settings:billing.pricing.tierFeatures.workspacesUnlimited",
    ),
    neroKiAssistent: t("settings:billing.pricing.tierFeatures.neroKiAssistent"),
    aiCredits50: t("settings:billing.pricing.tierFeatures.aiCredits50"),
    aiCredits100: t("settings:billing.pricing.tierFeatures.aiCredits100"),
    aiCreditsUnlimited: t(
      "settings:billing.pricing.tierFeatures.aiCreditsUnlimited",
    ),
    gapAnalysisBasic: t(
      "settings:billing.pricing.tierFeatures.gapAnalysisBasic",
    ),
    fullGapAnalysis: t("settings:billing.pricing.tierFeatures.fullGapAnalysis"),
    gapAnalysisAutoFix: t(
      "settings:billing.pricing.tierFeatures.gapAnalysisAutoFix",
    ),
    dashboardPdf: t("settings:billing.pricing.tierFeatures.dashboardPdf"),
    byoa: t("settings:billing.pricing.tierFeatures.byoa"),
    emailSupport: t("settings:billing.pricing.tierFeatures.emailSupport"),
    prioritySupport: t("settings:billing.pricing.tierFeatures.prioritySupport"),
    customerManager: t("settings:billing.pricing.tierFeatures.customerManager"),
    predictiveCompliance: t(
      "settings:billing.pricing.tierFeatures.predictiveCompliance",
    ),
    aiReports: t("settings:billing.pricing.tierFeatures.aiReports"),
  } as Record<string, string>;
}

function useTierNames() {
  const { t } = useTranslation(["settings"]);
  return {
    free: "Free",
    essentials: t("settings:billing.pricing.tierEssentials"),
    professional: t("settings:billing.pricing.tierProfessional"),
    unlimited: t("settings:billing.pricing.tierUnlimited"),
  } as Record<string, string>;
}

export function PlanChangeDialog({
  open,
  onClose,
  onConfirm,
  loading = false,
  currentTier,
  targetTier,
}: PlanChangeDialogProps) {
  const { t } = useTranslation(["settings", "common"]);
  const featureLabels = useFeatureLabels();
  const tierNames = useTierNames();

  const effectiveCurrent = currentTier === "free" ? "essentials" : currentTier;
  const isUpgrade = TIER_ORDER[targetTier] > TIER_ORDER[effectiveCurrent];

  const gainedFeatures = isUpgrade
    ? getGainedFeatures(currentTier, targetTier)
    : [];
  const lostFeatures = !isUpgrade
    ? getLostFeatures(currentTier, targetTier)
    : [];

  const targetName = tierNames[targetTier] ?? targetTier;

  return (
    <ConfirmDialog open={open} onClose={onClose}>
      <div className="flex justify-center pt-6">
        <img src="/brand/icon.svg" alt="Audinero" className="h-14 w-14" />
      </div>
      <ConfirmDialog.Title>
        {isUpgrade
          ? t("settings:billing.planChange.upgradeTitle", { plan: targetName })
          : t("settings:billing.planChange.downgradeTitle", {
              plan: targetName,
            })}
      </ConfirmDialog.Title>
      <ConfirmDialog.Body>
        <div className="space-y-4">
          <p className="text-[14px] text-secondary">
            {isUpgrade
              ? t("settings:billing.planChange.upgradeDescription")
              : t("settings:billing.planChange.downgradeDescription")}
          </p>

          {isUpgrade && gainedFeatures.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[13px] font-medium text-accent">
                <ArrowUp size={14} />
                {t("settings:billing.planChange.newFeatures")}
              </div>
              <div className="space-y-1.5 pl-1">
                {gainedFeatures.map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <Check size={14} className="text-accent shrink-0" />
                    <span className="text-[13px] text-secondary">
                      {featureLabels[key] ?? key}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isUpgrade && lostFeatures.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[13px] font-medium text-danger">
                <ArrowDown size={14} />
                {t("settings:billing.planChange.lostFeatures")}
              </div>
              <div className="space-y-1.5 pl-1">
                {lostFeatures.map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <X size={14} className="text-danger/60 shrink-0" />
                    <span className="text-[13px] text-secondary">
                      {featureLabels[key] ?? key}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ConfirmDialog.Body>
      <ConfirmDialog.Actions
        onConfirm={onConfirm}
        confirmLabel={
          isUpgrade
            ? t("settings:billing.planChange.confirmUpgrade")
            : t("settings:billing.planChange.confirmDowngrade")
        }
        cancelLabel={t("common:actions.cancel")}
        variant={isUpgrade ? "default" : "danger"}
        loading={loading}
      />
    </ConfirmDialog>
  );
}
